import { useState, useMemo, useEffect } from 'react'
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock'
import { appToast } from '../../../../../components/AppToast'
import { sendBatchSMS } from '../../api/studentsApi'

const MASK_OPTIONS = [
  { value: 'CLASES', label: 'CLASES' },
  { value: 'EDUINS', label: 'EDUINS' },
  { value: 'ACADEMY', label: 'ACADEMY' },
]

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

function formatDateInput(date) {
  const d = date.getDate().toString().padStart(2, '0')
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const y = date.getFullYear()
  return `${y}-${m}-${d}`
}

function toDisplayDate(isoDate) {
  if (!isoDate) return ''
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}

function countSmsParts(len) {
  if (len === 0) return 0
  return len <= 160 ? 1 : Math.ceil(len / 153)
}

export default function SendSMSModal({ isOpen, onClose, students = [] }) {
  const [message, setMessage] = useState('')
  const [selectedMask, setSelectedMask] = useState('CLASES')
  const [route, setRoute] = useState('promotional')
  const [isMultilingual, setIsMultilingual] = useState(false)
  const [sendToStudent, setSendToStudent] = useState(true)
  const [sendToFather, setSendToFather] = useState(true)
  const [sendToMother, setSendToMother] = useState(true)
  const [scheduleDate, setScheduleDate] = useState(() => formatDateInput(new Date()))
  const [scheduleHour, setScheduleHour] = useState('04')
  const [scheduleMinute, setScheduleMinute] = useState('15')
  const [scheduleMeridiem, setScheduleMeridiem] = useState('PM')
  const [sending, setSending] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const charCount = message.length
  const smsParts = countSmsParts(charCount)

  const recipients = useMemo(() => {
    const list = []
    students.forEach((s) => {
      if (sendToStudent && s.mobile) list.push({ name: s.firstName, mobile: s.mobile, role: 'Student' })
      if (sendToFather && s.guardianMobile) list.push({ name: s.guardianName || 'Father', mobile: s.guardianMobile, role: 'Father' })
      if (sendToMother && s.guardianMobile) list.push({ name: s.guardianName || 'Mother', mobile: s.guardianMobile, role: 'Mother' })
    })
    return list
  }, [students, sendToStudent, sendToFather, sendToMother])

  const selectedCount = recipients.length

  const handleSend = async () => {
    if (!message.trim()) {
      appToast.warning('Please enter a message.')
      return
    }
    if (recipients.length === 0) {
      appToast.warning('No recipients selected. Enable at least one Send To option.')
      return
    }
    setSending(true)
    try {
      const studentIds = students.map((s) => s.id)
      const res = await sendBatchSMS({
        studentIds,
        message: message.trim(),
        sendToStudent,
        sendToFather,
        sendToMother,
      })
      appToast.success(res.message || `SMS sent to ${recipients.length} recipient(s).`)
      onClose()
    } catch (err) {
      appToast.error(err?.response?.data?.detail || 'Failed to send batch SMS.')
    } finally {
      setSending(false)
    }
  }

  const handlePreview = () => {
    if (!message.trim()) {
      appToast.warning('Enter a message to preview.')
      return
    }
    const previewWindow = window.open('about:blank', '_blank', 'width=420,height=600')
    if (!previewWindow) return
    previewWindow.document.open()
    previewWindow.document.write(
      `<!doctype html><html><head><meta charset="utf-8" /><title>SMS Preview</title>
      <style>
        body{font-family:Arial,sans-serif;margin:0;padding:24px;background:#f5f5f5;color:#191c1e;}
        .phone{max-width:360px;margin:0 auto;border:2px solid #333;border-radius:24px;padding:16px;background:#fff;min-height:300px;}
        .header{text-align:center;font-size:11px;color:#666;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid #eee;}
        .msg{font-size:14px;line-height:1.5;white-space:pre-wrap;word-wrap:break-word;background:#e3f2fd;padding:12px 16px;border-radius:12px;border-bottom-right-radius:4px;}
        .meta{margin-top:16px;font-size:11px;color:#888;text-align:center;}
      </style></head><body>
      <div class="phone">
        <div class="header">SMS Preview</div>
        <div class="msg">${String(message).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
        <div class="meta">${charCount} chars &middot; ${smsParts} part${smsParts !== 1 ? 's' : ''} &middot; Route: ${route}</div>
      </div>
      </body></html>`,
    )
    previewWindow.document.close()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] max-h-[800px] w-full max-w-[1100px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-on-surface">Send SMS</h3>
            <p className="text-xs text-on-surface-variant">
              {selectedCount} entries selected &middot; Bulk SMS from current filter
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* ── Two-Column Body ────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* ── Left: Form Controls ──────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-5">
              {/* Message Header Row */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-bold text-on-surface">Message:</label>
                  <span className="text-xs font-bold text-on-surface-variant">
                    Entries selected: {selectedCount}
                  </span>
                </div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={7}
                  className="w-full resize-none rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-3 text-sm leading-relaxed text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Type your SMS message here..."
                />
                <div className="mt-1.5 flex items-center justify-between text-xs text-on-surface-variant">
                  <span>
                    {charCount} characters &middot; {smsParts} SMS part{smsParts !== 1 ? 's' : ''}
                    {charCount > 0 && (
                      <span className="ml-2 text-outline">
                        (Single: 160 | Multi: 153 chrs)
                      </span>
                    )}
                  </span>
                  <span className={smsParts > 1 ? 'font-bold text-amber-600' : ''}>
                    {smsParts > 1 ? 'Multi-part SMS' : smsParts === 1 ? 'Single SMS' : ''}
                  </span>
                </div>
              </div>

              {/* Mask Field Row */}
              <div className="flex items-center gap-3">
                <label className="shrink-0 text-sm font-bold text-on-surface">Mask:</label>
                <select
                  value={selectedMask}
                  onChange={(e) => setSelectedMask(e.target.value)}
                  className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2 text-sm font-medium text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {MASK_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-outline-variant/40 bg-surface-container-low text-on-surface-variant transition-colors hover:bg-primary/10 hover:text-primary"
                  title="Add new mask"
                >
                  <span className="material-symbols-outlined text-lg">add</span>
                </button>
              </div>

              {/* Route Radio Group */}
              <div className="flex items-center gap-3">
                <label className="shrink-0 text-sm font-bold text-on-surface">Route:</label>
                <div className="flex items-center gap-5">
                  {[
                    { key: 'promotional', label: 'Promotional' },
                    { key: 'transactional', label: 'Transactional' },
                    { key: 'open', label: 'Open' },
                  ].map((opt) => (
                    <label key={opt.key} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="smsRoute"
                        value={opt.key}
                        checked={route === opt.key}
                        onChange={() => setRoute(opt.key)}
                        className="h-4 w-4 text-primary focus:ring-primary/20"
                      />
                      <span className="text-sm text-on-surface">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Send To Options */}
              <div className="flex items-center gap-3">
                <label className="shrink-0 text-sm font-bold text-on-surface">Send To:</label>
                <div className="flex items-center gap-5">
                  {[
                    { key: 'student', label: 'Student', checked: sendToStudent, set: setSendToStudent },
                    { key: 'father', label: 'Father', checked: sendToFather, set: setSendToFather },
                    { key: 'mother', label: 'Mother', checked: sendToMother, set: setSendToMother },
                  ].map((opt) => (
                    <label key={opt.key} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={opt.checked}
                        onChange={(e) => opt.set(e.target.checked)}
                        className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20"
                      />
                      <span className="text-sm text-on-surface">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Template & Multilingual Row */}
              <div className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="text-sm font-bold text-primary hover:underline"
                >
                  Template
                </button>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isMultilingual}
                    onChange={(e) => setIsMultilingual(e.target.checked)}
                    className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20"
                  />
                  <span className="text-sm text-on-surface">Multilingual</span>
                </label>
              </div>

              {/* Template Dropdown (collapsible) */}
              {showTemplates && (
                <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                    Quick Templates
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'welcome', label: 'Welcome', body: 'Welcome to our institution! We are excited to have you on board.' },
                      { key: 'fee', label: 'Fee Reminder', body: 'Dear Student, this is a reminder that your fee payment is due. Please clear your dues at the earliest.' },
                      { key: 'exam', label: 'Exam Alert', body: 'Your examination is scheduled to begin shortly. Please ensure you have your admit card ready.' },
                      { key: 'holiday', label: 'Holiday Notice', body: 'The institution will remain closed on [DATE] on account of [HOLIDAY].' },
                    ].map((tpl) => (
                      <button
                        key={tpl.key}
                        type="button"
                        onClick={() => { setMessage(tpl.body); setShowTemplates(false) }}
                        className="rounded-lg bg-surface-container-high px-3 py-1.5 text-xs font-bold text-on-surface-variant transition-colors hover:bg-primary/10 hover:text-primary"
                      >
                        {tpl.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Schedule Controls */}
              <div className="flex flex-wrap items-center gap-3">
                <label className="shrink-0 text-sm font-bold text-on-surface">Schedule:</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <select
                  value={scheduleHour}
                  onChange={(e) => setScheduleHour(e.target.value)}
                  className="w-16 rounded-xl border border-outline-variant/40 bg-surface-container-low px-2 py-2 text-center text-sm font-medium text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {HOUR_OPTIONS.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span className="text-sm text-on-surface-variant">:</span>
                <select
                  value={scheduleMinute}
                  onChange={(e) => setScheduleMinute(e.target.value)}
                  className="w-16 rounded-xl border border-outline-variant/40 bg-surface-container-low px-2 py-2 text-center text-sm font-medium text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {MINUTE_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <select
                  value={scheduleMeridiem}
                  onChange={(e) => setScheduleMeridiem(e.target.value)}
                  className="w-20 rounded-xl border border-outline-variant/40 bg-surface-container-low px-2 py-2 text-center text-sm font-bold text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Divider ──────────────────────────────────────────────────── */}
          <div className="w-px bg-outline-variant/20" />

          {/* ── Right: SMS Rules Card ────────────────────────────────────── */}
          <div className="flex w-[340px] shrink-0 flex-col overflow-y-auto bg-surface-container-low px-6 py-5">
            <h4 className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wider text-on-surface">
              <span className="material-symbols-outlined text-lg text-primary">gavel</span>
              SMS Rules
            </h4>
            <div className="space-y-3 text-xs leading-relaxed text-on-surface-variant">
              <div className="rounded-xl bg-surface-container-lowest p-3">
                <p className="mb-1 font-bold text-on-surface">Promotional Route</p>
                <p>
                  As per TRAI guidelines, Promotional messages will be scrubbed for DND/NCPR registry.
                  Messages will not be delivered to numbers in Do-Not-Disturb (DND) list.
                  Promotional messages can be sent only from <strong>9am to 9pm</strong> and they will
                  not contain any Sender ID.
                </p>
              </div>

              <div className="rounded-xl bg-surface-container-lowest p-3">
                <p className="mb-1 font-bold text-on-surface">Transactional Route</p>
                <p>
                  Transactional messages are sent to all numbers including numbers on DND list.
                  Transactional messages cannot contain any promotional material. In order to ensure
                  this, transactional messages are compared with pre-defined message templates.
                </p>
              </div>

              <div className="rounded-xl bg-surface-container-lowest p-3">
                <p className="mb-1 font-bold text-on-surface">Open Route</p>
                <p>
                  Open messages are sent to all numbers including numbers on DND list. Open messages
                  cannot contain any phone numbers, email IDs, website addresses, promotional material
                  and certain words which are considered as promotional. <strong>Two credits</strong> are
                  deducted for each message sent from open route.
                </p>
              </div>

              <div className="rounded-xl border border-error/30 bg-error/5 p-3 text-error">
                <p className="text-[11px] font-bold">
                  Your account may get deactivated and additional fine/penalty may be imposed if you
                  send any promotional messages through open route.
                </p>
              </div>

              <div className="rounded-xl bg-surface-container-lowest p-3">
                <p className="mb-1 font-bold text-on-surface">Multilingual Support</p>
                <p>
                  Select Multilingual option to send messages in languages other than English. Standard
                  length for messages is:
                </p>
                <ul className="mt-1 list-inside list-disc space-y-0.5">
                  <li>English: Single 160 / Multipart 153 chrs</li>
                  <li>Multilingual: Single 70 / Multipart 67 chrs</li>
                </ul>
              </div>

              <div className="rounded-xl bg-surface-container-lowest p-3">
                <p className="mb-1 font-bold text-on-surface">DND / NCPR Registry</p>
                <p>
                  Numbers registered under National Do Not Disturb (DND) / National Customer Preference
                  Register (NCPR) will not receive promotional messages. Transactional and Open route
                  messages are delivered regardless of DND status.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-5 py-2.5 text-sm font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePreview}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-5 py-2.5 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-high"
          >
            <span className="flex items-center gap-2">
              <span className="material-symbols-outlined text-base">visibility</span>
              Preview
            </span>
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-on-primary shadow-sm transition-all hover:shadow-md disabled:opacity-60"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base">sms</span>
                Send
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
